import type { CSSProperties, ReactNode } from "react";
import { useWBConsent } from "./WBConsentProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { resolveWbChrome } from "../locale/wbChrome";
import { legalHref, PLATFORM_PRIVACY_URL } from "../components/legalLine";

// Click-to-load for third-party iframes.
//
// Renders `children` (the iframe) only once the provider is allowed; until
// then a placeholder in the SAME box — the caller hands over the very class
// string and style object the iframe would get, so giving consent does not
// move the page. The button is a per-provider session grant, which makes it
// the consent path on sites whose operator switched the banner off.

/** Display names for the hosts `mediaChildren` can embed. */
const PROVIDER_NAMES: Readonly<Record<string, string>> = {
  "www.youtube-nocookie.com": "YouTube",
  "youtube-nocookie.com": "YouTube",
  "www.youtube.com": "YouTube",
  "youtube.com": "YouTube",
  "youtu.be": "YouTube",
  "player.vimeo.com": "Vimeo",
  "vimeo.com": "Vimeo",
  "www.google.com": "Google Maps",
};

/** One consent key per PROVIDER, not per hostname: youtube.com and
 *  youtube-nocookie.com are the same recipient. */
export function providerOf(src: string): { key: string; name: string } {
  let host = "";
  try {
    host = new URL(src).host.toLowerCase();
  } catch {
    // unparseable ⇒ never allowed by accident: its own key, its own name
  }
  // Not `Object.hasOwn`: Safari < 15.4 lacks it, and a TypeError here would
  // take the whole page down through the app-level error boundary.
  const name = Object.prototype.hasOwnProperty.call(PROVIDER_NAMES, host)
    ? PROVIDER_NAMES[host]!
    : host || "external";
  return { key: name.toLowerCase(), name };
}

export function ConsentGate({
  src,
  kind,
  className,
  style,
  children,
}: {
  src: string;
  kind: "video" | "map";
  className: string | undefined;
  style: CSSProperties;
  children: ReactNode;
}) {
  const consent = useWBConsent();
  const { currentLang, primaryLanguage } = useWBLang();
  const { currentColorPalette } = useWBTheme();
  const provider = providerOf(src);
  if (consent.isAllowed(provider.key)) return <>{children}</>;

  const chrome = resolveWbChrome(currentLang, primaryLanguage);
  return (
    <div
      className={className}
      data-testid={`consent-placeholder-${kind}`}
      data-provider={provider.key}
      style={{
        ...style,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
        padding: "0.5rem",
        textAlign: "center",
        boxSizing: "border-box",
        // The box may GROW past the iframe's size, never clip: on a small
        // fixed height (h-16…h-40) or a narrow phone the button would be cut
        // off — and on a banner-less site that button is the only way to
        // consent. A small jump on tiny boxes beats an unreachable control.
        // (No overflow:hidden — it would also switch off the automatic
        // min-size of an aspect-ratio box.)
        minHeight: "min-content",
        minWidth: "min(100%, 14rem)",
        backgroundColor: "rgba(127,127,127,0.12)",
      }}
    >
      <button
        type="button"
        onClick={() => consent.grantOnce(provider.key)}
        className="px-4 py-2 text-sm font-medium cursor-pointer rounded-md"
        style={{
          backgroundColor: currentColorPalette.primary || "#222222",
          color: currentColorPalette.background || "#f2f2f2",
        }}
      >
        {chrome.loadContentFrom.replace("{provider}", provider.name)}
      </button>
      <p className="text-xs" style={{ maxWidth: "32rem", opacity: 0.8 }}>
        {chrome.consentNote}{" "}
        <a
          href={legalHref(consent.privacyPolicyUrl, PLATFORM_PRIVACY_URL)}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          {chrome.privacy}
        </a>
      </p>
    </div>
  );
}
